import React from 'react'
import PropTypes from 'prop-types'

/**
 * Displays an image, cropped or full.
 *
 * @param {Object} props - Component properties.
 * @param {string} [props.imageSrc] - Image URL.
 * @param {string} [props.croppedAreaAspect] - Selected frame size option.
 * @param {string} [props.originalAreaAspect] - Selected frame size option.
 * @param {Object} [props.croppedArea] - Crop area.
 * @returns {React.Element} Rendered image.
 */
const CroppedImage = React.memo(({ imageSrc, originalAreaAspect, croppedAreaAspect, croppedArea }) => {
  // Determine image styles based on the presence of croppedArea
  const imageStyle = React.useMemo(() => {
    if (!croppedArea) {
      // If croppedArea is not provided, display the original image
      return {
        width: '100%',
        height: 'auto',
        transform: 'none',
      }
    }

    // Calculate styles if croppedArea is provided
    const scale = 100 / croppedArea.width
    return {
      transform: `translate3d(${-croppedArea.x * scale}%, ${-croppedArea.y * scale}%, 0) scale3d(${scale}, ${scale}, 1)`,
      width: 'calc(100% + 0.5px)',
      height: 'auto',
    }
  }, [croppedArea])

  // Rendered aspect
  const aspect = React.useMemo(() => {
    if (croppedArea?.ratioInfo) {
      return croppedArea.ratioInfo.renderedRatioNum[0] / croppedArea.ratioInfo.renderedRatioNum[1]
    } else {
      return croppedAreaAspect
    }
  }, [croppedArea, croppedAreaAspect])

  return (
    <div
      className="output relative overflow-hidden shadow"
      style={{ paddingBottom: `${100 / (aspect || originalAreaAspect)}%` }}
    >
      <img
        className="absolute top-0 left-0 origin-top-left"
        src={imageSrc}
        alt={imageSrc}
        style={imageStyle}
      />
    </div>
  )
})

CroppedImage.displayName = 'CroppedImage'
CroppedImage.propTypes = {
  imageSrc: PropTypes.string,
  frameSize: PropTypes.object,
  croppedAreaAspect: PropTypes.number,
  originalAreaAspect: PropTypes.number,
  croppedArea: PropTypes.object, // croppedArea is optional
}

export default CroppedImage
